import { memo, useCallback } from 'react';
import { DatePicker, Validator } from 'cat-ecommerce-alloy';
import { useTranslation } from 'react-i18next';
import { useBreakpoint, useDrawer } from '@cat-ecom/pcc-components';
import { isPast } from 'date-fns/isPast';
import { isToday } from 'date-fns/isToday';
import { format } from 'date-fns/format';
import { parse } from 'date-fns/parse';
import PropTypes from 'prop-types';
import Field from '@app/components/common/form/Field';
import DrawerCalendar from '@app/components/common/DrawerCalendar/DrawerCalendar';
import { replaceTokensInString } from '@app/utils';
import { getIsOpenDay, getFulfillmentTypeFlag } from './utils';
import useGetMinDateInfo from '../hooks/useGetMinDateInfo';
import { useWebStorage } from '@app/hooks';
import { useFormikContext } from 'formik';
import shoppingStyles from '../shopping-cart/styles.module.scss';
import styles from './RequestbyDate.module.scss';
import { useSelector } from 'react-redux';
import { DATE_FNS_LOCALES } from '@app/constants/languageConstants';
import { PICKUP } from './constants';
import { PCC_CHECKOUT_PAGES_DRAWERS_MOBILE } from '@app/constants/featureFlags';
import { PAGE_BREAKPOINTS } from '@app/constants/commonConstants';
import { addYears, subDays } from 'date-fns';
import { PICKUP_REQUEST_DATE } from '../PickUpAndDelivery/constants';

const REQUEST_BY_DATE = 'requestByDate';
const SESSION = 'session';

const RequestByDate = ({
  fulfillmentType,
  handleChange,
  className,
  errorClassName,
  helpText,
  helpTextClassname,
  requestByDate,
  requestByDateRequired,
  labelName = 'REQUEST_BY_DATE',
  styleClassName,
  calendarClassName = '',
  availableByDate = '',
  labelClassName = '',
  isCalledFromModal = false,
  drawerFieldName = REQUEST_BY_DATE
}) => {
  const { t } = useTranslation();

  const drawerConfiguration = useDrawer({
    hasBackdrop: true
  });
  const isMD = useBreakpoint(PAGE_BREAKPOINTS.MD);

  const locale = useSelector(s => s.common?.locale);
  const storeHours = useSelector(
    s => s.checkout?.pickUp?.storeInfo?.storeHours
  );
  const isPccDisplayRBDByAvailability = useSelector(
    s => s?.featureFlag?.PCCDisplayRBDByAvailability
  );
  const isDeliveryDropboxRBDEnabled = useSelector(
    s => s.featureFlag?.PCCDeliveryDropboxRBDByAvailability
  );
  const isPCCCheckoutPagesDrawersMobile = useSelector(
    s => s.featureFlag[PCC_CHECKOUT_PAGES_DRAWERS_MOBILE]
  );

  const {
    setFieldError,
    setFieldTouched,
    errors,
    touched,
    values,
    setFieldValue
  } = useFormikContext();
  const [, setSavedRequestByDate] = useWebStorage(REQUEST_BY_DATE, SESSION);
  const cutOffType = getFulfillmentTypeFlag(fulfillmentType);
  const { minDate, minDateErrorMessage, isCutOffPassed } = useGetMinDateInfo({
    cutOffType,
    requestByDate,
    availableByDate
  });
  const today = new Date();
  const FIELD_REQUIRED_MSG = t('1626_FIELD_REQUIRED_ERROR');
  const maxDate = subDays(addYears(today, 1), 1);
  const { format: dateFormat, cutOffEnabled } = requestByDate;
  const rbdWasTouched = !!touched[REQUEST_BY_DATE];
  const isOpenDay = date => {
    if (
      storeHours &&
      fulfillmentType === PICKUP &&
      isPccDisplayRBDByAvailability
    ) {
      return getIsOpenDay(date, storeHours);
    }
    return true;
  };

  const validate = useCallback(
    (rbdValue, rbdWasTouched) => {
      let message = '';
      const hasTodaysCutoffTimePassed =
        cutOffEnabled && isToday(rbdValue) && isCutOffPassed;
      const isStoreOpen = isOpenDay(rbdValue);
      const isBeforeToday = !isToday(rbdValue) && isPast(rbdValue);
      // If rbd is touched alloy validation of datepicker will check the past dates, otherwise we need to validate past dates with rbd from xorder
      const hasAnErrorSelectedDate =
        (!rbdWasTouched && isBeforeToday) || hasTodaysCutoffTimePassed;

      if (!rbdValue && requestByDateRequired) {
        message = new Validator(rbdValue)
          .required(replaceTokensInString(FIELD_REQUIRED_MSG, t(labelName)))
          .getError();
      }

      if (rbdValue && (hasAnErrorSelectedDate || !isStoreOpen)) {
        message = t('REQUEST_BY_DATE_NO_VALID_ERROR');
      }

      return message;
    },
    [
      FIELD_REQUIRED_MSG,
      cutOffEnabled,
      isCutOffPassed,
      requestByDateRequired,
      t,
      isOpenDay
    ]
  );

  const onBlur = e => {
    const value = e.target.value;
    if (!value && !errors[REQUEST_BY_DATE] && requestByDateRequired) {
      const error = new Validator(value)
        .required(replaceTokensInString(FIELD_REQUIRED_MSG, t(labelName)))
        .getError();
      setFieldError(REQUEST_BY_DATE, error);
    }
    if (value) {
      const parsedDate = parse(value, dateFormat, new Date());
      const valueError = validate(parsedDate, rbdWasTouched);
      if (valueError) {
        setFieldError(REQUEST_BY_DATE, valueError);
      } else {
        // Note: remove web storage rbd value when legacy pick up and delivery has been modernized
        setSavedRequestByDate(parsedDate);
      }
    }
    setFieldTouched(REQUEST_BY_DATE, true, false);
  };

  const labelSpan = (
    <span className={shoppingStyles['shopping-cart-page__colon']}>:</span>
  );

  const getDefaultRequestByDate = useCallback(
    requestByDate => {
      if (!requestByDate) {
        return new Date();
      }
      const { value, format } = requestByDate;

      return !format ? requestByDate : parse(value, format, new Date());
    },
    [requestByDate]
  );

  const fieldValueDate = getDefaultRequestByDate(requestByDate);

  const selectedValue = values[drawerFieldName]
    ? values[drawerFieldName]
    : fieldValueDate;

  return isPCCCheckoutPagesDrawersMobile && !isMD && !isCalledFromModal ? (
    <DrawerCalendar
      drawerConfiguration={drawerConfiguration}
      activeDate={format(selectedValue, dateFormat)}
      activeDateFormat={dateFormat}
      label={[t(labelName), labelSpan]}
      drawerTitle={t('SELECT_A_REQUESTED_BY_DAY')}
      onChange={event => {
        handleChange(event.detail.rawDate);
        setFieldValue(REQUEST_BY_DATE, event.detail.rawDate);
        setFieldValue(drawerFieldName, event.detail.rawDate);
      }}
      shouldRenderCalendarField
      name={drawerFieldName}
      selectedValue={selectedValue ? format(selectedValue, dateFormat) : ''}
      validator={validate}
    />
  ) : (
    <Field
      as={DatePicker}
      locale={DATE_FNS_LOCALES[locale]}
      className={
        className || styles[styleClassName] || styles['datepicker-container']
      }
      dateFormat={dateFormat}
      errorClassName={errorClassName}
      helpText={helpText}
      helpTextClassname={helpTextClassname}
      id={REQUEST_BY_DATE}
      label={[t(labelName), labelSpan]}
      aria-label={t(labelName)}
      minDate={minDate}
      maxDate={maxDate}
      minDateErrorMessage={minDateErrorMessage}
      name={REQUEST_BY_DATE}
      onBlur={onBlur}
      skipFormikOnBlur={false}
      showRequired={requestByDateRequired}
      type="date"
      validator={validate}
      onChange={handleChange}
      filterDate={isOpenDay}
      popperClassName={calendarClassName}
      {...(isDeliveryDropboxRBDEnabled && { onFocus: e => e.target.blur() })}
      labelClassName={labelClassName}
    >
      <p
        className={`exclude-global px-3 text-xs ${styles['request-by-date_calendar-helper-text']}`}
      >
        {t('REQUEST_BY_DATE_DEF')}
      </p>
    </Field>
  );
};

export default memo(RequestByDate);

RequestByDate.propTypes = {
  className: PropTypes.string,
  errorClassName: PropTypes.string,
  fulfillmentType: PropTypes.string,
  isCalledFromModal: PropTypes.bool,
  labelName: PropTypes.string,
  handleChange: PropTypes.func,
  helpText: PropTypes.string,
  helpTextClassname: PropTypes.string,
  styleClassName: PropTypes.string,
  requestByDate: PropTypes.shape({
    cutOffEnabled: PropTypes.bool,
    cutOffTimes: PropTypes.shape({
      shopOnline: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
      pickup: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
      dropBox: PropTypes.oneOfType([PropTypes.string, PropTypes.bool])
    }),
    value: PropTypes.string,
    format: PropTypes.string
  }).isRequired,
  calendarClassName: PropTypes.string,
  requestByDateRequired: PropTypes.bool,
  availableByDate: PropTypes.string,
  labelClassName: PropTypes.string,
  drawerFieldName: PropTypes.string
};
